import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../lib/store';
import { formatPrice } from '../lib/utils';
import { User, Package, Clock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, getAuthHeader, logout } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
        setOrders(res.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated, navigate, getAuthHeader]);

  if (!isAuthenticated) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-accent/10 text-accent';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="paper-texture min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-xl border p-8 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-secondary" data-testid="account-name">{user?.name}</h1>
              <p className="text-muted-foreground" data-testid="account-email">{user?.email}</p>
              {user?.is_admin && (
                <span className="inline-block mt-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Admin
                </span>
              )}
            </div>
            <Button variant="outline" className="ml-auto" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-white rounded-xl border p-8">
          <div className="flex items-center gap-2 mb-6">
            <Package className="w-6 h-6 text-primary" />
            <h2 className="font-heading text-2xl font-semibold text-secondary">Order History</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No orders yet</p>
              <Button className="mt-4" onClick={() => navigate('/products')} data-testid="shop-now-btn">
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-6" data-testid="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="border rounded-lg p-6" data-testid={`order-${order.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Order #{order.id.slice(0, 8)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                      <span className="text-lg font-bold text-primary">{formatPrice(order.total)}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-secondary">{item.product_name} × {item.quantity}</span>
                        <span className="text-muted-foreground">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                    <p>Ships to: {order.shipping_name}, {order.shipping_address}, {order.shipping_city} {order.shipping_postal}, {order.shipping_country}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
